/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import {
  styled,
  Box,
  Button,
  Checkbox,
  IconButton,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { fonts } from "../../themes/dashboard";

const CONTROL_WIDTH = "200px";

export const PolicyDialogPaperProps = {
  sx: {
    borderRadius: "12px",
    display: "flex",
    flexDirection: "column",
    maxHeight: "90vh",
    overflow: "hidden",
    padding: 0,
  },
};

// While an edit waits for its policy the dialog is pinned to the height the
// loaded form takes (the form always overflows, so it settles at maxHeight), so
// the modal does not resize under the author once the loader gives way to fields.
export const LoadingPolicyDialogPaperProps = {
  sx: {
    ...PolicyDialogPaperProps.sx,
    height: "90vh",
  },
};

export const scrollableContent = {
  padding: "0px",
  overflowY: "auto",
};

// Takes the place of the scrollable content, so it fills exactly the space
// between the title bar and the action bar of the flex-column dialog paper.
export const ModalLoaderContainer = styled(Box)({
  alignItems: "center",
  display: "flex",
  flex: 1,
  justifyContent: "center",
});

export const TitleContainer = styled(Box)({
  alignItems: "center",
  borderBottom: "1px solid #e4e4e7",
  display: "flex",
  justifyContent: "space-between",
  padding: "16px 24px",
});

export const TitleText = styled(Typography)({
  color: "#18181b",
  fontFamily: fonts?.fontFamily,
  fontSize: "18px",
  fontWeight: "600",
  letterSpacing: "0px",
});

export const CloseIconButton = styled(IconButton)({
  color: "#71717a",
  padding: "4px",
  "&:hover": {
    color: "#18181b",
  },
});

export const SectionContainer = styled(Box)({
  borderBottom: "1px solid #e4e4e7",
  padding: "20px 24px",
});

export const SectionTitle = styled(Typography)({
  color: "#18181b",
  fontFamily: fonts?.fontFamily,
  fontSize: "15px",
  fontWeight: "600",
  letterSpacing: "0px",
});

export const SectionHelperText = styled(Typography)({
  color: "#8b7355",
  fontFamily: fonts?.fontFamily,
  fontSize: "12px",
  fontWeight: "400",
  letterSpacing: "0px",
  marginBottom: "12px",
});

export const FieldLabel = styled("label")({
  color: "#3f3f46",
  display: "block",
  fontFamily: fonts?.fontFamily,
  fontSize: "13px",
  fontWeight: "600",
  marginBottom: "6px",
});

export const FieldContainer = styled(Box)({
  marginBottom: "14px",
});

// Marks a field the create endpoint rejects the request without. Left readable to
// screen readers rather than aria-hidden, so the requirement is not sighted-only.
export const RequiredMark = styled("span")({
  color: "#d32f2f",
  marginLeft: "3px",
});

export const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "14px",
    "& fieldset": {
      borderColor: "#e4e4e7",
    },
    "&:hover fieldset": {
      borderColor: "#395bbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#395bbd",
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#a1a1aa",
    opacity: 1,
  },
});

export const StyledSelect = styled(Select)({
  borderRadius: "8px",
  fontSize: "14px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e4e4e7",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#395bbd",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#395bbd",
  },
});

export const dropdownMenuProps = (menuRef) => ({
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  PaperProps: { ref: menuRef, sx: { maxHeight: "40vh" } },
  autoFocus: false,
  MenuListProps: { sx: { pt: 0 } },
});

export const SelectPlaceholder = styled("span")({
  color: "#a1a1aa",
  fontSize: "14px",
});

export const HelperText = styled(Typography)({
  color: "#8b7355",
  fontFamily: fonts?.fontFamily,
  fontSize: "12px",
  fontWeight: "400",
  letterSpacing: "0px",
  marginTop: "6px",
});

export const ActivePolicyCheckText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isClear",
})(({ isClear }) => ({
  color: isClear ? "#2e7d32" : "#71717a",
  fontFamily: fonts?.fontFamily,
  fontSize: "12px",
  fontWeight: "400",
  letterSpacing: "0px",
  marginBottom: "6px",
}));

export const ErrorText = styled(Typography)({
  color: "#d32f2f",
  fontFamily: fonts?.fontFamily,
  fontSize: "12px",
  fontWeight: "400",
  letterSpacing: "0px",
  marginTop: "4px",
});

/* Draft banner */

export const DraftBanner = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDraft",
})(({ isDraft }) => ({
  alignItems: "center",
  backgroundColor: isDraft ? "#fef0c7" : "#fbfbfa",
  border: `1px solid ${isDraft ? "#f0c000" : "#e4e4e7"}`,
  borderRadius: "8px",
  display: "flex",
  gap: "10px",
  justifyContent: "space-between",
  padding: "10px 12px",
}));

export const DraftIconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDraft",
})(({ isDraft }) => ({
  alignItems: "center",
  color: isDraft ? "#a16207" : "#71717a",
  display: "flex",
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
  },
}));

export const DraftTitle = styled(Typography)({
  color: "#18181b",
  fontFamily: fonts?.fontFamily,
  fontSize: "13px",
  fontWeight: "600",
  letterSpacing: "0px",
});

export const DraftHelperText = styled(Typography)({
  color: "#71717a",
  fontFamily: fonts?.fontFamily,
  fontSize: "12px",
  fontWeight: "400",
  letterSpacing: "0px",
});

/* Policy type segmented control */

export const PolicyTypeGroup = styled(Box)({
  backgroundColor: "#f4f4f5",
  borderRadius: "8px",
  display: "inline-flex",
  gap: "2px",
  padding: "3px",
});

export const PolicyTypeOption = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})(({ isSelected }) => ({
  backgroundColor: isSelected ? "#ffffff" : "transparent",
  borderRadius: "6px",
  boxShadow: isSelected ? "0px 1px 2px rgba(0, 0, 0, 0.08)" : "none",
  color: isSelected ? "#18181b" : "#8b8b93",
  fontFamily: fonts?.fontFamily,
  fontSize: "13px",
  fontWeight: "600",
  letterSpacing: "0px",
  minWidth: "110px",
  padding: "6px 18px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: isSelected ? "#ffffff" : "#ebebed",
    boxShadow: isSelected ? "0px 1px 2px rgba(0, 0, 0, 0.08)" : "none",
  },
}));

/* Effective rules rows */

export const RuleRow = styled(Box)({
  alignItems: "center",
  borderBottom: "1px solid #f1f1f2",
  display: "flex",
  gap: "12px",
  justifyContent: "space-between",
  padding: "10px 0px",
  "&:last-of-type": {
    borderBottom: "none",
  },
});

export const RuleLabelContainer = styled(Box)({
  alignItems: "center",
  display: "flex",
  gap: "10px",
  minWidth: 0,
});

export const RuleIconWrapper = styled(Box)({
  alignItems: "center",
  color: "#71717a",
  display: "flex",
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
  },
});

export const RuleLabel = styled(Typography)({
  color: "#6b4f3a",
  fontFamily: fonts?.fontFamily,
  fontSize: "13px",
  fontWeight: "600",
  letterSpacing: "0px",
});

export const RuleControlContainer = styled(Box)({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  gap: "8px",
  justifyContent: "flex-end",
  width: CONTROL_WIDTH,
});

export const RuleStatus = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isSet",
})(({ isSet }) => ({
  color: isSet ? "#395bbd" : "#a1a1aa",
  flexShrink: 0,
  fontFamily: fonts?.fontFamily,
  fontSize: "11px",
  fontWeight: "500",
  letterSpacing: "0px",
  textAlign: "right",
  width: "48px",
}));

export const ToggleValueText = styled(Typography)({
  color: "#3f3f46",
  fontFamily: fonts?.fontFamily,
  fontSize: "13px",
  fontWeight: "400",
  letterSpacing: "0px",
});

/* Inactivity rules */

export const InactivitySection = styled(Box)({
  backgroundColor: "#fbfbfa",
  padding: "16px 24px",
});

export const InactivityHeader = styled(Box)({
  alignItems: "flex-start",
  display: "flex",
  gap: "12px",
  justifyContent: "space-between",
});

export const AddRuleButton = styled(Button)({
  borderColor: "#e4e4e7",
  borderRadius: "8px",
  color: "#18181b",
  flexShrink: 0,
  fontFamily: fonts?.fontFamily,
  fontSize: "13px",
  fontWeight: "600",
  letterSpacing: "0px",
  padding: "5px 14px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#f4f4f5",
    borderColor: "#d4d4d8",
  },
});

export const InactivityRuleCard = styled(Box)({
  backgroundColor: "#ffffff",
  border: "1px solid #e4e4e7",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  marginTop: "14px",
  padding: "12px 14px",
});

// The rule's delete control sits opposite the "Counts as activity" label so it
// reads as removing the whole rule, not just the period field it used to sit by.
export const InactivityRuleCardHeader = styled(Box)({
  alignItems: "center",
  display: "flex",
  gap: "12px",
  justifyContent: "space-between",
  marginBottom: "8px",
});

export const InactivityFieldLabel = styled(Typography)({
  color: "#71717a",
  fontFamily: fonts?.fontFamily,
  fontSize: "12px",
  fontWeight: "500",
  letterSpacing: "0px",
  marginBottom: "8px",
});

export const ActivityChipGroup = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
});

export const ActivityChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "hasError",
})(({ isSelected, hasError }) => ({
  alignItems: "center",
  backgroundColor: isSelected ? "#dbeafe" : "#ffffff",
  // The chips are the only required control with no MUI outline of its own, so the
  // error border is applied here to match what `error` does to the other fields.
  border: `1px solid ${hasError ? "#d32f2f" : (isSelected ? "#93c5fd" : "#e4e4e7")}`,
  borderRadius: "6px",
  color: isSelected ? "#1d4ed8" : "#3f3f46",
  cursor: "pointer",
  display: "inline-flex",
  fontFamily: fonts?.fontFamily,
  fontSize: "12px",
  fontWeight: "500",
  gap: "6px",
  padding: "5px 10px",
  userSelect: "none",
  "&:hover": {
    backgroundColor: isSelected ? "#dbeafe" : "#f4f4f5",
  },
}));

export const ActivityChipCheckbox = styled(Checkbox)({
  color: "#d4d4d8",
  padding: 0,
  "&.Mui-checked": {
    color: "#1d4ed8",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "15px",
  },
});

export const InactivityPeriodContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

export const InactivityPeriodRow = styled(Box)({
  alignItems: "center",
  display: "flex",
  gap: "8px",
});

export const InactivityPeriodUnit = styled(Typography)({
  color: "#71717a",
  fontFamily: fonts?.fontFamily,
  fontSize: "12px",
  fontWeight: "400",
  letterSpacing: "0px",
});

/* Fixed footer */

export const FixedActionContainer = styled(Box)({
  alignItems: "center",
  backgroundColor: "#ffffff",
  borderTop: "1px solid #e4e4e7",
  display: "flex",
  gap: "10px",
  justifyContent: "flex-end",
  padding: "14px 24px",
});

export const CreateButton = styled(Button)({
  "&.Mui-disabled": {
    backgroundColor: "#e0e0e0",
  },
  "&:hover": {
    backgroundColor: "#395bbd",
    boxShadow: "none",
  },
  backgroundColor: "#395bbd",
  boxShadow: "none",
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: "600",
  height: "35px",
  minWidth: "120px",
  padding: "0px 15px",
  textTransform: "none",
});
